import { useState, useRef, useEffect, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import client from '@/api/client';
import type { Hostel, Tenant, PageResponse, Bed, Room, TenantStatus } from '@/api/types';
import { UserPlus, Camera, Upload, X, CheckCircle2, Clock, Download, LogOut, Search } from 'lucide-react';
import { TenantExportDialog } from '@/components/TenantExportDialog';
import { SignTermsOnBehalfInline } from '@/components/SignTermsOnBehalfInline';
import { TENANT_STATUS_COLORS } from '@/features/tenant/tenantPortalShared';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/hooks/usePagination';
import { sanitizePhoneInput } from '@/lib/utils';
import { useTenantPhoneLookup } from '@/features/tenants/useTenantPhoneLookup';
import { TenantPhoneMatchDialog } from '@/features/tenants/TenantPhoneMatchDialog';

function PhotoCapture({ value, onChange, mode }: {
  value: string; onChange: (b64: string) => void; mode: 'square' | 'landscape';
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showCam, setShowCam] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const resize = (file: File): Promise<string> =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const c = document.createElement('canvas');
        if (mode === 'square') {
          c.width = 300; c.height = 300;
          const ctx = c.getContext('2d')!;
          const s = Math.min(img.width, img.height);
          ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 300, 300);
        } else {
          c.width = 800; c.height = 600;
          const ctx = c.getContext('2d')!;
          const scale = Math.min(800 / img.width, 600 / img.height);
          const sw = img.width * scale; const sh = img.height * scale;
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 800, 600);
          ctx.drawImage(img, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
        }
        URL.revokeObjectURL(url);
        resolve(c.toDataURL('image/jpeg', mode === 'square' ? 0.85 : 0.9));
      };
      img.src = url;
    });

  useEffect(() => {
    if (stream && videoRef.current && showCam) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, showCam]);

  const openCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: mode === 'square'
          ? { facingMode: 'user', width: 640, height: 640 }
          : { facingMode: 'environment', width: 1280, height: 720 },
      });
      setStream(s); setShowCam(true);
    } catch { fileRef.current?.click(); }
  };

  const capture = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    if (mode === 'square') {
      c.width = 300; c.height = 300;
      const ctx = c.getContext('2d')!;
      const s = Math.min(v.videoWidth, v.videoHeight);
      ctx.drawImage(v, (v.videoWidth - s) / 2, (v.videoHeight - s) / 2, s, s, 0, 0, 300, 300);
    } else {
      c.width = 800; c.height = 600;
      const ctx = c.getContext('2d')!;
      const scale = Math.min(800 / v.videoWidth, 600 / v.videoHeight);
      const sw = v.videoWidth * scale; const sh = v.videoHeight * scale;
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 800, 600);
      ctx.drawImage(v, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
    }
    const b64 = c.toDataURL('image/jpeg', mode === 'square' ? 0.85 : 0.9);
    stream?.getTracks().forEach(t => t.stop());
    setStream(null); setShowCam(false);
    onChange(b64);
  };

  return (
    <>
      <div className={`flex flex-col gap-1.5 ${mode === 'square' ? 'items-center' : ''}`}>
        {value ? (
          <div className={`relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${mode === 'square' ? 'w-20 h-20' : 'w-full'}`}
            style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined}>
            <img src={value} alt="Captured" className="w-full h-full object-contain" />
            <button type="button" onClick={() => onChange('')}
              className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">✕</button>
          </div>
        ) : (
          <div className={`rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300 ${mode === 'square' ? 'w-20 h-20' : 'w-full'}`}
            style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined}>
            <Camera size={mode === 'square' ? 20 : 28} />
          </div>
        )}
        <div className="flex gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 text-[11px] border border-gray-300 px-2 py-1 rounded-md hover:bg-gray-50 text-gray-600">
            <Upload size={10} /> {t('supervisor.upload', 'Upload')}
          </button>
          <button type="button" onClick={openCam}
            className="flex items-center gap-1 text-[11px] border border-violet-300 text-violet-700 px-2 py-1 rounded-md hover:bg-violet-50">
            <Camera size={10} /> {t('supervisor.camera', 'Camera')}
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={async e => { const f = e.target.files?.[0]; if (f) { onChange(await resize(f)); } e.target.value = ''; }} />
      <canvas ref={canvasRef} className="hidden" />
      {showCam && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
          <div className="bg-chrome-page rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <p className="font-semibold text-sm">{t('supervisor.capturePhoto', 'Capture Photo')}</p>
              <button type="button" onClick={() => { stream?.getTracks().forEach(t => t.stop()); setStream(null); setShowCam(false); }}
                className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
            </div>
            <div className="p-3 space-y-2">
              <video ref={videoRef} className={`w-full rounded-lg bg-black ${mode === 'square' ? 'aspect-square' : ''}`}
                style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined} muted playsInline />
              <button type="button" onClick={capture}
                className="w-full bg-violet-600 text-white py-2 rounded-xl text-sm font-semibold">📷 {t('supervisor.capture', 'Capture')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function SupervisorTenantsPage({ role = 'supervisor' }: { role?: 'owner' | 'supervisor' }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: hostels = [] } = useQuery<Hostel[]>({
    queryKey: ['my-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });

  // Seed initial filters from the URL (e.g. deep-linked from the dashboard); this page is
  // shared by both `/owner/tenants` and `/supervisor/tenants`, so read search loosely.
  const initialSearch = useSearch({ strict: false }) as Partial<{
    hostelId: string; status: string; joiningDate: string; checkoutFrom: string; checkoutTo: string;
  }>;

  const [hostelId, setHostelId] = useState(initialSearch.hostelId ?? '');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | ''>((initialSearch.status as TenantStatus) ?? 'ACTIVE');
  const [joiningDate, setJoiningDate] = useState(initialSearch.joiningDate ?? '');
  const [checkoutFrom, setCheckoutFrom] = useState(initialSearch.checkoutFrom ?? '');
  const [checkoutTo, setCheckoutTo] = useState(initialSearch.checkoutTo ?? '');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const activeHostel = hostelId;

  const { page, size, setPage, resetPage } = usePagination();

  function runSearch() {
    setSearchQuery(searchInput.trim());
    resetPage();
  }

  const { data, isLoading, isError, error } = useQuery<PageResponse<Tenant>>({
    queryKey: ['tenants', activeHostel, statusFilter, joiningDate, checkoutFrom, checkoutTo, searchQuery, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (statusFilter) params.set('status', statusFilter);
      if (activeHostel) params.set('hostelId', activeHostel);
      if (searchQuery) params.set('search', searchQuery);
      if (joiningDate) params.set('joiningDate', joiningDate);
      if (checkoutFrom) params.set('checkoutFrom', checkoutFrom);
      if (checkoutTo) params.set('checkoutTo', checkoutTo);
      return client.get(`/api/tenants?${params}`).then(r => r as unknown as PageResponse<Tenant>);
    },
  });

  function goToTenant(tenant: Tenant) {
    if (role === 'owner') {
      void navigate({
        to: '/owner/hostels/$id/tenants/$tenantId',
        params: { id: tenant.hostelId, tenantId: tenant.id },
        search: { tab: 'profile' },
      });
    } else {
      void navigate({
        to: '/supervisor/hostels/$id/tenants/$tenantId',
        params: { id: tenant.hostelId, tenantId: tenant.id },
        search: { tab: 'profile' },
      });
    }
  }

  const { data: rooms = [] } = useQuery<Room[]>({
    queryKey: ['rooms', activeHostel],
    queryFn: () => client.get(`/api/rooms/hostel/${activeHostel}`).then(r => r as unknown as Room[]),
    enabled: !!activeHostel,
  });

  const [addForm, setAddForm] = useState({ fullName: '', phone: '', email: '', gender: '', dateOfBirth: '', roomId: '', bedId: '', joiningDate: '', expectedCheckoutDate: '', monthlyRent: '', securityDeposit: '', govIdType: '', occupation: '', companyOrInstitution: '', permanentAddress: '', city: '', state: '', pincode: '', emergencyContactName: '', emergencyContactPhone: '', emergencyContactRel: '', fatherName: '', fatherContact: '', fatherOccupation: '', motherName: '', motherContact: '', motherOccupation: '' });
  const [addPhotos, setAddPhotos] = useState({ photoUrl: '' });
  const [addGovIds, setAddGovIds] = useState([{ govIdType: '', frontPhotoUrl: '', backPhotoUrl: '' }]);
  const [showAdd, setShowAdd] = useState(false);
  const [reactivateTenantId, setReactivateTenantId] = useState<string | null>(null);
  const phoneLookup = useTenantPhoneLookup();

  async function handlePhoneBlur() {
    await phoneLookup.lookup(addForm.phone);
  }

  function handlePrefillBasic(fullName: string, email: string) {
    setAddForm(p => ({ ...p, fullName: p.fullName || fullName, email: p.email || email }));
    phoneLookup.reset();
  }

  function handleFetchDetails(tenant: Tenant) {
    setAddForm({
      fullName: tenant.fullName, phone: tenant.phone, email: tenant.email ?? '',
      gender: tenant.gender ?? '', dateOfBirth: tenant.dateOfBirth ?? '',
      roomId: '', bedId: '', joiningDate: '', expectedCheckoutDate: '',
      monthlyRent: tenant.monthlyRent != null ? String(tenant.monthlyRent) : '',
      securityDeposit: tenant.securityDeposit != null ? String(tenant.securityDeposit) : '',
      govIdType: tenant.govIdType ?? '', occupation: tenant.occupation ?? '',
      companyOrInstitution: tenant.companyOrInstitution ?? '', permanentAddress: tenant.permanentAddress ?? '',
      city: tenant.city ?? '', state: tenant.state ?? '', pincode: tenant.pincode ?? '',
      emergencyContactName: tenant.emergencyContactName ?? '', emergencyContactPhone: tenant.emergencyContactPhone ?? '',
      emergencyContactRel: tenant.emergencyContactRel ?? '',
      fatherName: tenant.fatherName ?? '', fatherContact: tenant.fatherContact ?? '', fatherOccupation: tenant.fatherOccupation ?? '',
      motherName: tenant.motherName ?? '', motherContact: tenant.motherContact ?? '', motherOccupation: tenant.motherOccupation ?? '',
    });
    setAddPhotos({ photoUrl: tenant.photoUrl ?? '' });
    setReactivateTenantId(tenant.id);
    phoneLookup.reset();
  }
  const [exportOpen, setExportOpen] = useState(false);
  const [signOpen, setSignOpen] = useState<string | null>(null);
  const activeHostelObj = hostels.find(h => h.id === activeHostel) ?? null;

  const { data: roomBeds = [] } = useQuery<Bed[]>({
    queryKey: ['beds', addForm.roomId],
    queryFn: () => client.get(`/api/rooms/${addForm.roomId}/beds`).then(r => r as unknown as Bed[]),
    enabled: !!addForm.roomId,
  });

  const addMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => client.post('/api/tenants', body),
    onSuccess: () => {
      setShowAdd(false);
      setReactivateTenantId(null);
      phoneLookup.reset();
      void qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => client.post(`/api/tenants/${id}/checkout`, { checkoutDate: new Date().toISOString().slice(0, 10) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenants'] }),
  });

  const tenants = data?.content ?? [];
  const vacantBeds = roomBeds.filter(b => b.status === 'VACANT');

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <h1 className="text-2xl font-bold text-gray-900">{t('tenant.title')}</h1>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.search', 'Search')}</label>
            <div className="flex items-stretch">
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
                placeholder={t('supervisor.searchNameEmail', 'Search name / email / phone…')}
                className="h-9 border border-gray-300 rounded-l-lg px-3 text-sm w-48"
              />
              <button
                onClick={runSearch}
                aria-label={t('common.search', 'Search')}
                className="h-9 w-9 flex items-center justify-center border border-l-0 border-gray-300 rounded-r-lg text-gray-500 hover:bg-gray-50 shrink-0"
              >
                <Search size={15} />
              </button>
            </div>
          </div>
          {hostels.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('hostel.hostelName')}</label>
              <Select
                value={hostelId}
                onChange={e => { setHostelId(e.target.value); resetPage(); }}
                className="w-auto h-9"
                options={[{ value: '', label: t('common.all') }, ...hostels.map(h => ({ value: h.id, label: h.name }))]}
              />
            </div>
          )}
          {activeHostel && (
            <button
              onClick={() => setExportOpen(true)}
              className="h-9 flex items-center gap-1.5 border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 rounded-lg text-sm font-medium transition"
            >
              <Download size={15} /> {t('export.button', 'Export')}
            </button>
          )}
          {activeHostel && (
            <button onClick={() => setShowAdd(!showAdd)} className="h-9 flex items-center gap-2 bg-violet-700 text-white px-3 rounded-lg text-sm font-medium">
              <UserPlus size={15} /> {t('supervisor.admit', 'Admit')}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.status')}</label>
          <Select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as TenantStatus | ''); resetPage(); }}
            className="w-auto h-9"
            options={[
              { value: '', label: t('common.all') },
              { value: 'ACTIVE', label: t('common.active') },
              { value: 'RESERVED', label: t('common.reserved') },
              { value: 'CHECKED_OUT', label: t('common.checkedOut') },
              { value: 'CANCELLED', label: t('common.cancelled') },
            ]}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('supervisor.joinedOnFilter', 'Joined On')}</label>
          <input
            type="date"
            value={joiningDate}
            onChange={e => { setJoiningDate(e.target.value); resetPage(); }}
            className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('supervisor.leavingFrom', 'Leaving From')}</label>
          <input
            type="date"
            value={checkoutFrom}
            onChange={e => { setCheckoutFrom(e.target.value); resetPage(); }}
            className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('supervisor.leavingTo', 'Leaving To')}</label>
          <input
            type="date"
            value={checkoutTo}
            onChange={e => { setCheckoutTo(e.target.value); resetPage(); }}
            className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
          />
        </div>
        {(joiningDate || checkoutFrom || checkoutTo) && (
          <button
            onClick={() => { setJoiningDate(''); setCheckoutFrom(''); setCheckoutTo(''); resetPage(); }}
            className="h-9 text-xs text-gray-500 hover:text-gray-700 underline"
          >
            {t('common.clear')}
          </button>
        )}
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
          {t('supervisor.failedToLoadTenants', 'Failed to load tenants:')} {(error as Error)?.message ?? t('supervisor.unknownError', 'Unknown error')}
        </div>
      )}

      {exportOpen && (
        <TenantExportDialog
          hostelId={activeHostel}
          status="ACTIVE"
          accent="violet"
          onClose={() => setExportOpen(false)}
        />
      )}

      {phoneLookup.result && (
        <TenantPhoneMatchDialog
          result={phoneLookup.result}
          role={role}
          currentHostelId={activeHostel}
          onFetchDetails={handleFetchDetails}
          onPrefillBasic={handlePrefillBasic}
          onStartFresh={() => phoneLookup.reset()}
          onClose={() => phoneLookup.reset()}
        />
      )}

      {showAdd && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-semibold mb-4">{t('supervisor.admitNewTenant', 'Admit New Tenant')}</h2>
          <form
            onSubmit={e => {
              e.preventDefault();
              addMutation.mutate({
                hostelId: activeHostel,
                bedId: addForm.bedId,
                fullName: addForm.fullName,
                phone: addForm.phone,
                email: addForm.email || undefined,
                gender: addForm.gender || undefined,
                dateOfBirth: addForm.dateOfBirth || undefined,
                govIdType: addForm.govIdType || undefined,
                occupation: addForm.occupation || undefined,
                companyOrInstitution: addForm.companyOrInstitution || undefined,
                permanentAddress: addForm.permanentAddress || undefined,
                city: addForm.city || undefined,
                state: addForm.state || undefined,
                pincode: addForm.pincode || undefined,
                emergencyContactName: addForm.emergencyContactName || undefined,
                emergencyContactPhone: addForm.emergencyContactPhone || undefined,
                emergencyContactRelation: addForm.emergencyContactRel || undefined,
                joiningDate: addForm.joiningDate,
                expectedCheckoutDate: addForm.expectedCheckoutDate || undefined,
                monthlyRent: Number(addForm.monthlyRent),
                securityDeposit: Number(addForm.securityDeposit) || 0,
                photoUrl: addPhotos.photoUrl || undefined,
                govIds: addGovIds.filter(g => g.frontPhotoUrl || g.backPhotoUrl || g.govIdType).map(g => ({
                  govIdType: g.govIdType || undefined,
                  frontPhotoUrl: g.frontPhotoUrl || undefined,
                  backPhotoUrl: g.backPhotoUrl || undefined,
                })),
                fatherName: addForm.fatherName || undefined,
                fatherContact: addForm.fatherContact || undefined,
                fatherOccupation: addForm.fatherOccupation || undefined,
                motherName: addForm.motherName || undefined,
                motherContact: addForm.motherContact || undefined,
                motherOccupation: addForm.motherOccupation || undefined,
                reactivateTenantId: reactivateTenantId || undefined,
              });
            }}
            className="space-y-4"
          >
            {/* Profile photo + basic info */}
            <div className="flex items-start gap-4 pb-4 border-b border-gray-100">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">{t('supervisor.profilePhoto', 'Profile Photo')}</p>
                <PhotoCapture value={addPhotos.photoUrl} onChange={v => setAddPhotos(p => ({ ...p, photoUrl: v }))} mode="square" />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fullNameRequired', 'Full Name *')}</label>
                  <input required value={addForm.fullName} onChange={e => setAddForm(p => ({ ...p, fullName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.phoneRequired', 'Phone *')}</label>
                  <input type="tel" inputMode="tel" required value={addForm.phone}
                    onChange={e => { setAddForm(p => ({ ...p, phone: sanitizePhoneInput(e.target.value) })); setReactivateTenantId(null); }}
                    onBlur={() => void handlePhoneBlur()}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                  {phoneLookup.loading ? (
                    <p className="text-[11px] text-gray-400 mt-1">{t('tenant.checkingPhone', 'Checking…')}</p>
                  ) : reactivateTenantId ? (
                    <p className="text-[11px] text-teal-600 mt-1">{t('tenant.reactivatingPreviousStay', 'Reactivating this tenant’s previous profile.')}</p>
                  ) : (
                    <p className="text-[11px] text-gray-400 mt-1">{t('tenant.phoneSearchHint', 'Enter phone number to search for an existing profile.')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.gender', 'Gender')}</label>
                  <Select
                    value={addForm.gender ?? ''}
                    onChange={e => setAddForm(p => ({ ...p, gender: e.target.value }))}
                    options={[
                      { value: '', label: t('supervisor.select', 'Select') },
                      { value: 'MALE', label: t('supervisor.male', 'Male') },
                      { value: 'FEMALE', label: t('supervisor.female', 'Female') },
                      { value: 'OTHER', label: t('supervisor.other', 'Other') },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Personal */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.email')}</label>
                <input type="email" value={addForm.email ?? ''} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.dateOfBirth', 'Date of Birth')}</label>
                <input type="date" value={addForm.dateOfBirth ?? ''} onChange={e => setAddForm(p => ({ ...p, dateOfBirth: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.occupation', 'Occupation')}</label>
                <input value={addForm.occupation} onChange={e => setAddForm(p => ({ ...p, occupation: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.companyOrInstitution', 'Company / Institution')}</label>
                <input value={addForm.companyOrInstitution} onChange={e => setAddForm(p => ({ ...p, companyOrInstitution: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

            {/* Address */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('common.address')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.permanentAddress', 'Permanent Address')}</label>
                  <textarea value={addForm.permanentAddress} onChange={e => setAddForm(p => ({ ...p, permanentAddress: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.city', 'City')}</label>
                  <input value={addForm.city} onChange={e => setAddForm(p => ({ ...p, city: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.state', 'State')}</label>
                  <input value={addForm.state} onChange={e => setAddForm(p => ({ ...p, state: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.pincode', 'Pincode')}</label>
                  <input value={addForm.pincode} onChange={e => setAddForm(p => ({ ...p, pincode: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* ID & Financials */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.idAndFinancials', 'ID & Financials')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.govIdType', 'Gov ID Type')}</label>
                  <Select
                    value={addForm.govIdType}
                    onChange={e => setAddForm(p => ({ ...p, govIdType: e.target.value }))}
                    options={[
                      { value: '', label: '—' },
                      ...['AADHAAR','PASSPORT','DRIVING_LICENSE','VOTER_ID','NATIONAL_ID','OTHER'].map(idType => ({ value: idType, label: idType })),
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.roomRequired', 'Room *')}</label>
                  <Select
                    required
                    value={addForm.roomId}
                    onChange={e => setAddForm(p => ({ ...p, roomId: e.target.value, bedId: '' }))}
                    options={[
                      { value: '', label: t('supervisor.selectRoom', 'Select room') },
                      ...rooms.filter(r => r.status === 'ACTIVE').map(r => ({ value: r.id, label: t('supervisor.roomNumberLabel', 'Room {{number}}', { number: r.roomNumber }) })),
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.bedRequired', 'Bed *')}</label>
                  <Select
                    required
                    value={addForm.bedId}
                    onChange={e => setAddForm(p => ({ ...p, bedId: e.target.value }))}
                    disabled={!addForm.roomId}
                    options={[
                      { value: '', label: t('supervisor.selectBed', 'Select bed') },
                      ...vacantBeds.map(b => ({ value: b.id, label: t('supervisor.bedLabel', 'Bed {{label}}', { label: b.bedLabel }) })),
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.joiningDateRequired', 'Joining Date *')}</label>
                  <input type="date" required value={addForm.joiningDate} onChange={e => setAddForm(p => ({ ...p, joiningDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.monthlyRentRequired', 'Monthly Rent *')}</label>
                  <input type="number" required value={addForm.monthlyRent} onChange={e => setAddForm(p => ({ ...p, monthlyRent: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.securityDeposit', 'Security Deposit')}</label>
                  <input type="number" value={addForm.securityDeposit} onChange={e => setAddForm(p => ({ ...p, securityDeposit: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.expectedCheckoutDate', 'Expected Checkout Date')}</label>
                  <input type="date" value={addForm.expectedCheckoutDate} onChange={e => setAddForm(p => ({ ...p, expectedCheckoutDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.emergencyContact', 'Emergency Contact')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.name')}</label>
                  <input value={addForm.emergencyContactName} onChange={e => setAddForm(p => ({ ...p, emergencyContactName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.phone')}</label>
                  <input type="tel" inputMode="tel" value={addForm.emergencyContactPhone} onChange={e => setAddForm(p => ({ ...p, emergencyContactPhone: sanitizePhoneInput(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.relation', 'Relation')}</label>
                  <input value={addForm.emergencyContactRel} onChange={e => setAddForm(p => ({ ...p, emergencyContactRel: e.target.value }))} placeholder={t('supervisor.relationPlaceholder', 'e.g. Father, Spouse')} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Government IDs */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.governmentIds', 'Government IDs')}</p>
              <div className="space-y-4">
                {addGovIds.map((g, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3 relative bg-gray-50/40">
                    {addGovIds.length > 1 && (
                      <button type="button" onClick={() => setAddGovIds(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-3 right-3 text-gray-400 hover:text-red-500"><X size={14} /></button>
                    )}
                    <Select
                      value={g.govIdType}
                      onChange={e => setAddGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, govIdType: e.target.value } : x))}
                      className="mr-6"
                      options={[
                        { value: '', label: t('supervisor.idTypePlaceholder', '— ID Type —') },
                        ...['AADHAAR','PASSPORT','DRIVING_LICENSE','VOTER_ID','NATIONAL_ID','OTHER'].map(idType => ({ value: idType, label: idType })),
                      ]}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1.5">{t('supervisor.frontSide', 'Front Side')}</p>
                        <PhotoCapture value={g.frontPhotoUrl} onChange={v => setAddGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, frontPhotoUrl: v } : x))} mode="landscape" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1.5">{t('supervisor.backSide', 'Back Side')}</p>
                        <PhotoCapture value={g.backPhotoUrl} onChange={v => setAddGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, backPhotoUrl: v } : x))} mode="landscape" />
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => setAddGovIds(prev => [...prev, { govIdType: '', frontPhotoUrl: '', backPhotoUrl: '' }])}
                  className="flex items-center gap-1.5 text-sm text-violet-700 border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50">
                  ＋ {t('supervisor.addAnotherId', 'Add Another ID')}
                </button>
              </div>
            </div>

            {/* Parent / Guardian */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.parentGuardianDetails', 'Parent / Guardian Details')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 text-xs font-medium text-gray-500 -mb-1">{t('supervisor.fathersDetails', "Father's Details")}</div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fathersName', "Father's Name")}</label>
                  <input value={addForm.fatherName} onChange={e => setAddForm(p => ({ ...p, fatherName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fathersContact', "Father's Contact")}</label>
                  <input type="tel" inputMode="tel" value={addForm.fatherContact} onChange={e => setAddForm(p => ({ ...p, fatherContact: sanitizePhoneInput(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fathersOccupation', "Father's Occupation")}</label>
                  <input value={addForm.fatherOccupation} onChange={e => setAddForm(p => ({ ...p, fatherOccupation: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2 text-xs font-medium text-gray-500 mt-1 -mb-1">{t('supervisor.mothersDetails', "Mother's Details")}</div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.mothersName', "Mother's Name")}</label>
                  <input value={addForm.motherName} onChange={e => setAddForm(p => ({ ...p, motherName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.mothersContact', "Mother's Contact")}</label>
                  <input type="tel" inputMode="tel" value={addForm.motherContact} onChange={e => setAddForm(p => ({ ...p, motherContact: sanitizePhoneInput(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.mothersOccupation', "Mother's Occupation")}</label>
                  <input value={addForm.motherOccupation} onChange={e => setAddForm(p => ({ ...p, motherOccupation: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={addMutation.isPending} className="bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {addMutation.isPending ? t('supervisor.admitting', 'Admitting…') : t('supervisor.admitTenant', 'Admit Tenant')}
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="border px-5 py-2 rounded-lg text-sm">{t('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">{t('common.loading')}</p>
          </div>
        ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">{t('common.name')}</th>
              <th className="px-4 py-3 text-left">{t('common.phone')}</th>
              <th className="px-4 py-3 text-left">{t('supervisor.joined', 'Joined')}</th>
              <th className="px-4 py-3 text-left">{t('supervisor.rent', 'Rent')}</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">{t('common.status')}</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">{t('supervisor.agreement', 'Agreement')}</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tenants.map(tn => (
              <Fragment key={tn.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => goToTenant(tn)}>
                  <td className="px-4 py-3 font-medium">
                    {tn.fullName}
                    {tn.email && <p className="text-xs text-gray-400 font-normal">{tn.email}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{tn.phone}</td>
                  <td className="px-4 py-3 text-gray-500">{tn.joiningDate}</td>
                  <td className="px-4 py-3 font-semibold">₹{Number(tn.monthlyRent).toLocaleString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${TENANT_STATUS_COLORS[tn.status] || 'bg-gray-100 text-gray-600'}`}>
                      {tn.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {tn.termsAccepted
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700"><CheckCircle2 size={10} /> {t('supervisor.signed', 'Signed')}</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700"><Clock size={10} /> {t('rent.pending')}</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-left whitespace-nowrap space-x-3" onClick={e => e.stopPropagation()}>
                    {!tn.termsAccepted && tn.status === 'ACTIVE' && (
                      <button
                        onClick={() => setSignOpen(signOpen === tn.id ? null : tn.id)}
                        className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium hover:underline"
                      >
                        <Clock size={12} /> {t('tenant.signOnBehalfAction', 'Sign Terms')}
                      </button>
                    )}
                    {tn.status === 'ACTIVE' && (
                      <button
                        onClick={() => { if (confirm(t('supervisor.confirmCheckoutName', 'Check out {{name}}?', { name: tn.fullName }))) checkoutMutation.mutate({ id: tn.id }); }}
                        className="inline-flex items-center gap-1 text-red-500 text-xs font-medium hover:underline"
                      >
                        <LogOut size={12} /> {t('supervisor.checkout', 'Checkout')}
                      </button>
                    )}
                  </td>
                </tr>
                {signOpen === tn.id && (
                  <tr>
                    <td colSpan={7} className="px-4 pb-4">
                      <SignTermsOnBehalfInline
                        tenantId={tn.id}
                        hostel={activeHostelObj}
                        onDone={() => { setSignOpen(null); void qc.invalidateQueries({ queryKey: ['tenants'] }); }}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        )}
        {!isLoading && tenants.length === 0 && (
          <p className="text-center text-gray-400 py-10">{t('common.noData')}</p>
        )}
        <Pagination
          page={page}
          totalPages={data?.totalPages ?? 0}
          totalElements={data?.totalElements ?? 0}
          size={size}
          onPageChange={setPage}
          className="px-4 pb-3"
        />
      </div>
    </div>
  );
}
